import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { mqMin, space, tokPx } from '@sunwebgroup/ui-components'
import { Button, focusOutline, Icon, Sheet } from '@sunwebgroup/ui-components/uds'
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps'
import Image from 'next/image'
import * as React from 'react'
import { styled } from 'styled-components'
import activeMarker from './assets/location-marker--accommodation-active.svg'
import inactiveMarker from './assets/location-marker--nearby-accommodation.svg'
import mapPlaceholder from './assets/map-placeholder.svg'
import { useDestinationMapData } from './hooks'
import { MapAccoCard } from './MapAccoCard'

const Container = styled.div`
  position: relative;
  flex-grow: 1;
  min-height: 100px;
  width: 100%;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    min-height: ${space('primitives_space_40')};
  }
`

const StyledButton = styled(Button)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: max-content;
`

const StyledSheetContent = styled(Sheet.Content)`
  height: 100%;
  width: 100%;
  position: relative;
`

const StyledImage = styled(Image)`
  object-fit: cover;
  border-radius: ${tokPx('primitives_corner_radius_l')};
`

const MapDetails = styled.div`
  display: flex;
  position: absolute;
  left: ${tokPx('primitives_space_16')};
  bottom: ${tokPx('primitives_space_16')};
  gap: ${tokPx('primitives_space_16')};
`

const OverlayButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${tokPx('primitives_corner_radius_l')};

  ${focusOutline()}
`

const DEST_MAP_ACCO_MAP_ID = '46b6aa5f650c9fa7175e5400' as const

interface DestinationMapProperties extends React.HTMLAttributes<HTMLDivElement> {
  isHeroSection?: boolean
}

function DestinationMap({ className, isHeroSection = false }: DestinationMapProperties) {
  const intl = useIntl()
  const accommodationId = useCMSPageField('PDP.id')
  const [activeAccoId, setActiveAccoId] = React.useState<string | null>(accommodationId)
  const { lat, lng } = useCMSPageField('PDP.coordinates')
  const { nearbyAccos } = useDestinationMapData()
  const { sendEvent } = useAnalytics()
  const [isSheetOpen, setIsSheetOpen] = React.useState(false)

  const handleOpenChange = () => {
    const nextOpen = !isSheetOpen

    sendEvent(
      createPDPClickEvent({
        element: 'Bottom sheet',
        description: `${isHeroSection ? 'Top - ' : ''}Destination map bottom sheet ${nextOpen ? 'opened' : 'closed'}`,
        result: `Destination map bottom sheet ${nextOpen ? 'opened' : 'closed'}`,
      }),
    )

    setIsSheetOpen(nextOpen)
  }

  return (
    <Container className={className}>
      <StyledImage src={mapPlaceholder} alt="" fill />
      <OverlayButton onClick={handleOpenChange} />
      <Sheet.Root open={isSheetOpen} onOpenChange={handleOpenChange}>
        <Sheet.Trigger>
          <StyledButton $size="S" $type="overlayNeutral">
            <Icon glyph="MapPinOnMap" />
            {intl.formatMessage({ id: 'destination_map_open_button' })}
          </StyledButton>
        </Sheet.Trigger>
        <Sheet.Body side="bottom" hasPadding={false}>
          <Sheet.Header
            closeLabel={intl.formatMessage({ id: 'close' })}
            titleLabel={intl.formatMessage({ id: 'destination_map_title' })}
          />
          <StyledSheetContent>
            <APIProvider apiKey={process.env.NEXT_PUBLIC_GMAPS_API_KEY ?? ''}>
              <Map
                mapId={DEST_MAP_ACCO_MAP_ID}
                defaultZoom={12}
                defaultCenter={{ lat, lng }}
                reuseMaps
                fullscreenControl={false}
                headingInteractionEnabled={false}
                clickableIcons={false}
              >
                <AdvancedMarker
                  position={{ lat, lng }}
                  onClick={() => {
                    setActiveAccoId(accommodationId)
                  }}
                >
                  <Image src={activeMarker} alt="" />
                </AdvancedMarker>

                {nearbyAccos?.map(acco => (
                  <AdvancedMarker
                    key={acco.id}
                    position={{ lat: acco.lat, lng: acco.lng }}
                    onClick={() => setActiveAccoId(acco.id)}
                  >
                    <Image src={inactiveMarker} alt="" />
                  </AdvancedMarker>
                ))}
              </Map>
            </APIProvider>
            <MapDetails>{activeAccoId && <MapAccoCard accoId={activeAccoId} />}</MapDetails>
          </StyledSheetContent>
        </Sheet.Body>
      </Sheet.Root>
    </Container>
  )
}

export default styled(DestinationMap)``
